import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { Link } from "react-router";
import { useMe, useSettings } from "../hooks.ts";
import { Badge, Button, Card, cx, Dialog, ErrorText, Field, Input, PageHeader, Spinner } from "../components/ui.tsx";
import { Md } from "../components/Markdown.tsx";
import { browserZone, describeSchedule, relative } from "../lib/cron.ts";

interface Job {
  id: string;
  name?: string;
  prompt?: string;
  schedule_display?: string;
  schedule?: { display?: string; expr?: string; kind?: string } | string;
  skills?: string[] | null;
  enabled?: boolean;
  state?: string;
  deliver?: string;
  created_at?: string;
  next_run_at?: string | null;
  last_run_at?: string | null;
  last_status?: string | null;
  last_error?: string | null;
}

const scheduleText = (j: Job) => j.schedule_display ?? (typeof j.schedule === "string" ? j.schedule : j.schedule?.display ?? j.schedule?.expr ?? "");
const when = (s?: string | null) => (s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");
const isPaused = (j: Job) => j.enabled === false || j.state === "paused";

function useJobActions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["jobs"] });
      void qc.invalidateQueries({ queryKey: ["job-runs"] });
    },
  });
}
const runJob = (id: string, action: "pause" | "resume" | "run") => unwrap(api.me.assistant.jobs[":id"][":action{pause|resume|run}"].$post({ param: { id, action } }));

export function TasksPage() {
  const me = useMe();
  const settings = useSettings();
  const zone = settings.data?.timezone ?? null;
  const allowed = me.data?.group?.permissions.cronJobs ?? false;
  const jobs = useQuery({ queryKey: ["jobs"], queryFn: () => unwrap(api.me.assistant.jobs.$get()), refetchInterval: 30_000 });
  const [editing, setEditing] = useState<Job | "new" | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const act = useJobActions();
  const list = (jobs.data?.jobs ?? []) as unknown as Job[];
  const current = list.find((j) => j.id === open) ?? null;

  return (
    <>
      <PageHeader
        title="Scheduled tasks"
        description="Things your assistant does on a schedule — a morning briefing, a weekly report. Open a task to see every run and its full log."
        actions={allowed ? <Button variant="primary" onClick={() => setEditing("new")}>New task</Button> : undefined}
      />
      {settings.data && <ZoneNote zone={zone} />}
      {!allowed && (
        <Card className="mb-4 p-4 text-sm text-[var(--muted)]">
          Scheduled tasks are switched off for your group, so your tasks are paused and can't be changed, run or resumed. You can still delete them. They resume on their own if an admin switches scheduled tasks back on.
        </Card>
      )}
      <ErrorText error={jobs.error ?? act.error} />
      {jobs.isPending && <div className="grid place-items-center py-12"><Spinner /></div>}
      {jobs.data && !list.length && (
        <Card className="px-5 py-12 text-center text-sm text-[var(--muted)]">No scheduled tasks yet. You can also ask your assistant in chat, e.g. “every weekday at 9am, summarise my inbox”.</Card>
      )}
      <div className="grid gap-3 lg:grid-cols-2">
        {list.map((j) => {
          const paused = isPaused(j);
          return (
            <Card key={j.id} className="flex flex-col p-5 transition hover:ring-1 hover:ring-[var(--color-accent)]/30">
              <button className="min-w-0 flex-1 text-left" onClick={() => setOpen(j.id)}>
                <div className="flex items-start gap-2">
                  <span className="min-w-0 flex-1 font-semibold leading-snug">{j.name || "Untitled task"}</span>
                  {paused ? <Badge>Paused</Badge> : <Badge tone="good">Active</Badge>}
                </div>
                <div className="mt-2 text-sm font-medium text-[var(--color-accent)]">{describeSchedule(scheduleText(j), zone)}</div>
                <p className="mt-2 line-clamp-2 text-sm text-[var(--muted)]">{j.prompt}</p>
              </button>
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-[var(--line)] pt-3 text-xs text-[var(--muted)]">
                <span>Next: {paused ? "—" : <span title={when(j.next_run_at)}>{relative(j.next_run_at)}</span>}</span>
                <span>
                  Last: {j.last_run_at ? <span title={when(j.last_run_at)}>{relative(j.last_run_at)}</span> : "never"}
                  {j.last_status && <Badge tone={j.last_status === "ok" ? "good" : "bad"}>{j.last_status === "ok" ? "OK" : j.last_status}</Badge>}
                </span>
                <span className="ml-auto flex gap-1">
                  {allowed && <Button variant="ghost" className="h-7 text-xs" disabled={act.isPending} onClick={() => act.mutate(() => runJob(j.id, "run"))}>Run now</Button>}
                  <Button variant="ghost" className="h-7 text-xs" disabled={act.isPending || (paused && !allowed)} onClick={() => act.mutate(() => runJob(j.id, paused ? "resume" : "pause"))}>{paused ? "Resume" : "Pause"}</Button>
                  <Button variant="ghost" className="h-7 text-xs" onClick={() => setOpen(j.id)}>Details</Button>
                </span>
              </div>
            </Card>
          );
        })}
      </div>
      {current && <TaskDetail job={current} allowed={allowed} onEdit={() => setEditing(current)} onClose={() => setOpen(null)} />}
      {editing && <TaskDialog job={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

/* ── Detail: overview + runs ─────────────────────────────────────────────── */

function TaskDetail({ job, allowed, onEdit, onClose }: { job: Job; allowed: boolean; onEdit: () => void; onClose: () => void }) {
  const [tab, setTab] = useState<"overview" | "runs">("overview");
  const act = useJobActions();
  const paused = isPaused(job);
  const expr = scheduleText(job);
  const zone = useSettings().data?.timezone ?? null;
  return (
    <Dialog
      open
      onClose={onClose}
      wide="xl"
      title={
        <span className="flex items-center gap-2">
          {job.name || "Untitled task"} {paused ? <Badge>Paused</Badge> : <Badge tone="good">Active</Badge>}
        </span>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-[var(--line)] pb-3">
        {(["overview", "runs"] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)} className={cx("rounded-md px-3 py-1.5 text-sm", tab === t ? "bg-[var(--panel-2)] font-medium" : "text-[var(--muted)] hover:text-[var(--text)]")}>
            {t === "overview" ? "Overview" : "Runs & logs"}
          </button>
        ))}
        <span className="ml-auto flex gap-1">
          {allowed && <Button className="h-8" disabled={act.isPending} onClick={() => act.mutate(() => runJob(job.id, "run"))}>Run now</Button>}
          <Button className="h-8" disabled={act.isPending || (paused && !allowed)} onClick={() => act.mutate(() => runJob(job.id, paused ? "resume" : "pause"))}>{paused ? "Resume" : "Pause"}</Button>
          {allowed && <Button className="h-8" onClick={onEdit}>Edit</Button>}
          <Button
            variant="danger"
            className="h-8"
            onClick={() => confirm(`Delete “${job.name || "this task"}”?`) && act.mutate(async () => { await unwrap(api.me.assistant.jobs[":id"].$delete({ param: { id: job.id } })); onClose(); })}
          >
            Delete
          </Button>
        </span>
      </div>
      <ErrorText error={act.error} />
      {act.isSuccess && !act.isPending && <p className="mb-3 text-xs text-[var(--muted)]">Done. A manual run shows up under Runs & logs within a few seconds.</p>}
      {tab === "overview" ? (
        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Schedule" value={describeSchedule(expr, zone)} sub={<span className="font-mono">{expr}</span>} />
            <Stat label="Next run" value={paused ? "Paused" : relative(job.next_run_at)} sub={paused ? "" : when(job.next_run_at)} />
            <Stat
              label="Last run"
              value={job.last_run_at ? relative(job.last_run_at) : "Never"}
              sub={job.last_status ? <Badge tone={job.last_status === "ok" ? "good" : "bad"}>{job.last_status === "ok" ? "Succeeded" : job.last_status}</Badge> : when(job.last_run_at)}
            />
          </div>
          {job.last_error && <p className="rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-700">Last error: {job.last_error}</p>}
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--muted)]">What it does</h3>
            <div className="rounded-lg border border-[var(--line)] px-4 py-3">
              <Md>{job.prompt ?? ""}</Md>
            </div>
          </section>
          {!!job.skills?.length && (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--muted)]">Skills it uses</h3>
              <div className="flex flex-wrap gap-1.5">{job.skills.map((s) => <Badge key={s}>{s}</Badge>)}</div>
            </section>
          )}
          {job.created_at && <p className="text-xs text-[var(--muted)]">Created {when(job.created_at)}</p>}
        </div>
      ) : (
        <RunsTab jobId={job.id} />
      )}
    </Dialog>
  );
}

/** Which zone schedules run in, and a way to change it. */
function ZoneNote({ zone }: { zone: string | null }) {
  const local = browserZone();
  return (
    <p className="mb-4 text-xs text-[var(--muted)]">
      {zone ? <>Your assistant runs tasks on <b className="text-[var(--text)]">{zone}</b> time{zone !== local && <> (this browser is on {local}; times below are shown in your browser's time)</>}.</> : <>Your assistant runs tasks on <b className="text-[var(--text)]">UTC</b> until you choose your time zone.</>}{" "}
      <Link to="/settings" className="font-medium text-[var(--text)] underline">Change in Settings</Link>
    </p>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-[var(--line)] px-4 py-3">
      <div className="text-xs text-[var(--muted)]">{label}</div>
      <div className="mt-1 font-semibold">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-[var(--muted)]">{sub}</div>}
    </div>
  );
}

const PAGE = 10;
const runTone = { completed: "good", failed: "bad", running: "accent", claimed: "accent", unknown: "neutral" } as const;
const runLabel = { completed: "Succeeded", failed: "Failed", running: "Running", claimed: "Starting", unknown: "Unknown" } as const;

function duration(a: string | null, b: string | null) {
  if (!a || !b) return "";
  const s = Math.max(0, (new Date(b).getTime() - new Date(a).getTime()) / 1000);
  return s < 60 ? `${Math.round(s)} s` : `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`;
}

function RunsTab({ jobId }: { jobId: string }) {
  const [page, setPage] = useState(0);
  const [openRun, setOpenRun] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["job-runs", jobId, page],
    queryFn: () => unwrap(api.me.assistant.jobs[":id"].runs.$get({ param: { id: jobId }, query: { limit: String(PAGE), offset: String(page * PAGE) } })),
    refetchInterval: (query) => (query.state.data?.runs.some((r) => r.status === "running" || r.status === "claimed") ? 4000 : 20000),
  });
  if (q.isPending) return <div className="grid place-items-center py-10"><Spinner /></div>;
  if (q.error) return <ErrorText error={q.error} />;
  const { total, runs } = q.data;
  if (!total) return <p className="py-10 text-center text-sm text-[var(--muted)]">No runs yet. Use “Run now” to try it.</p>;
  return (
    <div>
      <ul className="divide-y divide-[var(--line)] rounded-lg border border-[var(--line)]">
        {runs.map((r) => (
          <li key={r.id}>
            <button className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left text-sm hover:bg-[var(--panel-2)]" onClick={() => setOpenRun(openRun === r.id ? null : r.id)}>
              <span className="w-4 text-[var(--muted)]">{openRun === r.id ? "▾" : "▸"}</span>
              <span className="min-w-44">{when(r.startedAt)}</span>
              <Badge tone={r.trigger === "manual" ? "accent" : "neutral"}>{r.trigger === "manual" ? "Manual" : "Scheduled"}</Badge>
              <Badge tone={runTone[r.status]}>{runLabel[r.status]}</Badge>
              <span className="text-xs text-[var(--muted)]">{duration(r.startedAt, r.finishedAt)}</span>
              {r.error && <span className="min-w-0 flex-1 truncate text-xs text-red-700">{r.error}</span>}
            </button>
            {openRun === r.id && (
              <div className="border-t border-[var(--line)] bg-[var(--panel-2)]/50 px-4 py-3">
                {r.sessionId ? <RunLog sessionId={r.sessionId} /> : <p className="text-sm text-[var(--muted)]">{r.status === "claimed" ? "Starting…" : "No log was recorded for this run."}</p>}
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-3 flex items-center justify-between text-sm">
        <span className="text-[var(--muted)]">
          {page * PAGE + 1}–{page * PAGE + runs.length} of {total}
        </span>
        <span className="flex gap-2">
          <Button disabled={page === 0} onClick={() => setPage(page - 1)}>Newer</Button>
          <Button disabled={(page + 1) * PAGE >= total} onClick={() => setPage(page + 1)}>Older</Button>
        </span>
      </div>
    </div>
  );
}

/** A run's full conversation: the task prompt, each tool call and result, and the final answer. */
export function RunLog({ sessionId }: { sessionId: string }) {
  const q = useQuery({ queryKey: ["run-log", sessionId], queryFn: () => unwrap(api.me.assistant.sessions[":id"].messages.$get({ param: { id: sessionId }, query: { limit: "500" } })) });
  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorText error={q.error} />;
  return (
    <ol className="max-h-[50vh] space-y-2 overflow-y-auto pr-1">
      {q.data.messages.map((m) => (
        <li key={m.id} className="text-sm">
          {m.role === "user" ? (
            <details className="rounded-md border border-[var(--line)] bg-[var(--panel)] px-3 py-2">
              <summary className="cursor-pointer text-xs font-medium text-[var(--muted)]">Task instructions</summary>
              <div className="mt-2 whitespace-pre-wrap text-xs">{m.content}</div>
            </details>
          ) : m.role === "tool" ? (
            <details className="rounded-md bg-[var(--panel)] px-3 py-1.5 ring-1 ring-[var(--line)]">
              <summary className="cursor-pointer font-mono text-xs text-[var(--muted)]">↳ result from {m.toolName ?? "tool"} · {(m.content ?? "").length.toLocaleString()} chars</summary>
              <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-[11px]">{(m.content ?? "").slice(0, 20000)}</pre>
            </details>
          ) : (
            <div className="rounded-md bg-[var(--panel)] px-3 py-2 ring-1 ring-[var(--line)]">
              {m.toolCalls.length > 0 && <div className="mb-1 font-mono text-xs text-[var(--color-accent)]">⚙ {m.toolCalls.join(", ")}</div>}
              {m.content && <Md>{m.content}</Md>}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}

/* ── Create / edit ───────────────────────────────────────────────────────── */

const EXAMPLES = ["every weekday at 9am", "every monday 8am", "every 2h", "0 18 * * 5"];

function TaskDialog({ job, onClose }: { job: Job | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(job?.name ?? "");
  const [schedule, setSchedule] = useState(job ? scheduleText(job) : "every weekday at 9am");
  const [prompt, setPrompt] = useState(job?.prompt ?? "");
  const zone = useSettings().data?.timezone ?? null;
  const save = useMutation({
    mutationFn: () =>
      job
        ? unwrap(api.me.assistant.jobs[":id"].$patch({ param: { id: job.id }, json: { name, schedule, prompt } }))
        : unwrap(api.me.assistant.jobs.$post({ json: { name, schedule, prompt } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["jobs"] });
      onClose();
    },
  });
  return (
    <Dialog open onClose={onClose} title={job ? "Edit task" : "New scheduled task"} wide>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Field label="Name"><Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Morning briefing" /></Field>
        <Field
          label="When"
          hint={
            <>
              Plain English or cron (times are in {zone ?? "UTC"}). {schedule && describeSchedule(schedule, zone) !== schedule && <b>{describeSchedule(schedule, zone)}. </b>}Examples:{" "}
              {EXAMPLES.map((x, i) => <button type="button" key={x} onClick={() => setSchedule(x)} className="font-mono text-[var(--color-accent)] hover:underline">{x}{i < EXAMPLES.length - 1 ? ", " : ""}</button>)}
            </>
          }
        >
          <Input required value={schedule} onChange={(e) => setSchedule(e.target.value)} className="font-mono" />
        </Field>
        <Field label="What should your assistant do?">
          <textarea required rows={10} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Summarise my unread email and today's calendar in five bullet points." className="w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 py-2 text-sm outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20" />
        </Field>
        <ErrorText error={save.error} />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={save.isPending}>Save</Button>
        </div>
      </form>
    </Dialog>
  );
}
